import {useContext, useEffect, useRef} from 'react';
import {NetworkContext} from '../../lib/context';
import {ProviderRostersStatus} from '../../lib/stores/roster-store';
import {safeConsoleError} from '../../../../lib/sanitize';

type ProviderRosterList = {
    status: ProviderRostersStatus;
    // Loads the list again: a retry after a failure, or a refresh of a loaded list
    reload: () => void;
};

/**
 * Keeps the provider roster list loaded while the calling component is mounted, so a view that
 * needs the list never depends on another component to fetch it. Loads whenever nothing is loaded
 * or in flight - including after another caller aborted the request this one shared. The store
 * shares one request between callers, so any number of components can use this at once.
 *
 * The caller must be a MobX observer, so it re-renders when the status changes.
 *
 * @param onError - Called when a request this caller started fails; by default the error is only
 *                  logged, since the status already reports it
 */
export function useProviderRosterList(
    onError?: (error: unknown, retry: () => void) => void,
): ProviderRosterList {
    const networkOptimizationStore = useContext(NetworkContext);
    const status = networkOptimizationStore.providerRostersStatus;

    // One controller per mount: unmounting cancels this caller's request, while a reload or a
    // status change never cancels one in flight
    const controllerRef = useRef<AbortController | null>(null);
    useEffect(() => {
        const controller = new AbortController();
        controllerRef.current = controller;
        return () => controller.abort();
    }, []);

    const reload = () => {
        if (!controllerRef.current) {
            return;
        }
        networkOptimizationStore.loadProviderRosters(controllerRef.current).catch((error: unknown) => {
            if (onError) {
                onError(error, reload);
            } else {
                safeConsoleError('Failed to load provider rosters:', error);
            }
        });
    };

    useEffect(() => {
        if (status === 'idle') {
            reload();
        }
    }, [status]);

    return {status, reload};
}
